import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown, ChevronRight, Copy, ExternalLink, Files, FolderInput, Globe, LogOut,
  MessageSquare, Moon, Pencil, Search, Shield, Sun, Trash2,
} from 'lucide-react';
import { api, type Doc, type User } from '../lib/api';
import { toggle, useTheme } from '../lib/theme';
import { R, ft, label, mono, shadow } from '../lib/ui';
import { Avatar, Kbd, Label, MenuItem, MenuSeparator, Popover } from './Primitives';
import { useContextMenu, type MenuEntry } from './ContextMenu';
import { useDialog } from './DialogHost';
import { useToast } from './Toast';
import { DocPropertiesModal } from './DocPropertiesModal';
import { docIcon } from '../lib/doc-icons';

const ROLE_HINT: Record<User['role'], string> = {
  admin: 'Администратор · полный доступ',
  editor: 'Редактор',
  viewer: 'Только чтение',
  none: 'Только чтение',
};

/**
 * Боковая панель — раскладка из восстановленного исходника: 260 px,
 * двойная граница справа, поиск по Ctrl+K, документы сгруппированы по
 * разделам, бесхозные падают в «Прочее».
 */
export function Sidebar({
  me, docs, loading, onNavigate,
}: {
  me?: User;
  docs: Doc[];
  loading: boolean;
  onNavigate: (path: string) => void;
}) {
  const [location] = useLocation();
  const qc = useQueryClient();
  const theme = useTheme();

  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);

  const menu = useContextMenu();
  const dialog = useDialog();
  const toast = useToast();

  const searchRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const term = query.trim().toLowerCase();
  const sections = useQuery({ queryKey: ['sections'], queryFn: api.sections }).data ?? [];

  const canEdit = me?.role === 'admin' || me?.role === 'editor';

  const invalidateDocs = () => {
    qc.invalidateQueries({ queryKey: ['docs'] });
    qc.invalidateQueries({ queryKey: ['sections'] });
  };

  const duplicateDoc = useMutation({
    mutationFn: (v: { docId: string; name?: string }) => api.duplicateDoc(v.docId, { name: v.name }),
    onSuccess: (newDoc) => {
      invalidateDocs();
      toast('Копия таблицы создана в том же разделе', 'ok');
      onNavigate(`/d/${newDoc.id}`);
    },
    onError: (e) => toast(`Ошибка создания копии: ${(e as Error).message}`, 'err'),
  });

  const removeDoc = useMutation({
    mutationFn: (docId: string) => api.deleteDoc(docId),
    onSuccess: invalidateDocs,
  });

  const moveDoc = useMutation({
    mutationFn: (v: { docId: string; sectionId: string }) =>
      api.updateDoc(v.docId, { sectionId: v.sectionId }),
    onSuccess: invalidateDocs,
  });

  /** Пункты меню документа — состав зависит от прав. */
  const docMenu = (doc: Doc): MenuEntry[] => [
    { type: 'header', label: doc.name },
    { label: 'Открыть', icon: ExternalLink, onClick: () => onNavigate(`/d/${doc.id}`) },
    ...(canEdit
      ? [
          { label: 'Изменить', icon: Pencil, onClick: () => setEditing(doc.id) } as MenuEntry,
          {
            label: 'Создать копию',
            icon: Copy,
            onClick: async () => {
              const copyName = await dialog.prompt({
                title: 'Создать копию таблицы',
                value: `${doc.name} (копия)`,
              });
              if (copyName === null) return;
              duplicateDoc.mutate({ docId: doc.id, name: copyName.trim() || undefined });
            },
          } as MenuEntry,
        ]
      : []),
    ...(canEdit && sections.length > 1
      ? [
          {
            label: 'Переместить в раздел',
            icon: FolderInput,
            submenu: sections.map((s) => ({
              label: s.name,
              disabled: s.id === doc.sectionId,
              onClick: () => moveDoc.mutate({ docId: doc.id, sectionId: s.id }),
            })),
          } as MenuEntry,
        ]
      : []),
    ...(canEdit
      ? [
          { type: 'divider' } as MenuEntry,
          {
            label: 'Удалить',
            icon: Trash2,
            danger: true,
            onClick: async () => {
              const ok = await dialog.confirm({
                title: 'Удалить документ?',
                body: `«${doc.name}» и все его таблицы будут удалены. Восстановить нельзя.`,
                danger: true,
              });
              if (ok) removeDoc.mutate(doc.id);
            },
          } as MenuEntry,
        ]
      : []),
  ];

  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSettled: () => {
      qc.setQueryData(['me'], null);
      window.location.assign('/');
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.code === 'KeyK') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const groups = useMemo(() => {
    const visible = term ? docs.filter((d) => d.name.toLowerCase().includes(term)) : docs;

    const bySection = new Map<string | null, Doc[]>();
    for (const doc of visible) {
      const list = bySection.get(doc.sectionId);
      if (list) list.push(doc);
      else bySection.set(doc.sectionId, [doc]);
    }

    const known = sections.map((s) => ({
      section: { id: s.id, name: s.name },
      docs: bySection.get(s.id) ?? [],
    }));

    const orphans = [...bySection.keys()]
      .filter((id) => !sections.some((s) => s.id === id))
      .map((id) => ({
        section: { id: id ?? '__none__', name: 'Прочее' },
        docs: bySection.get(id) ?? [],
      }));

    return [...known, ...orphans].filter((g) => g.docs.length > 0);
  }, [docs, sections, term]);

  const atRoot = location === '/';

  const toggleSection = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <aside
      style={{
        width: 260,
        flex: '0 0 260px',
        background: R.bgSubtle,
        borderRight: ft.strong,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <button
        onClick={() => onNavigate('/')}
        title="Все документы"
        style={{
          padding: '16px 16px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = R.bgElevated)}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <img
          src="/favicon.png"
          alt=""
          style={{ width: 30, height: 30, display: 'block', flex: '0 0 30px' }}
        />
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
          <span style={{ fontWeight: 800, fontSize: 14, letterSpacing: '-.01em', color: R.text }}>
            Redwood Tables
          </span>
          <span style={label}>GTA5RP</span>
        </span>
      </button>

      <div style={{ padding: '0 12px 12px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 36,
            padding: '0 10px',
            background: R.bg,
            border: ft.edge,
          }}
        >
          <Search size={14} color={R.textMuted} style={{ flex: '0 0 14px' }} />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setQuery('');
                e.currentTarget.blur();
              }
            }}
            placeholder="Поиск по документам"
            style={{
              flex: 1,
              minWidth: 0,
              width: 0,
              fontSize: 13,
              border: 'none',
              background: 'transparent',
              color: R.text,
              textOverflow: 'ellipsis',
            }}
          />
          {!query && (
            <span style={{ display: 'flex', gap: 3 }}>
              <Kbd>Ctrl</Kbd>
              <Kbd>K</Kbd>
            </span>
          )}
        </div>
      </div>

      <div style={{ height: 2, background: R.border, marginBottom: 4 }} />

      <nav className="rt-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <button
          onClick={() => onNavigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '12px 16px 8px',
            textAlign: 'left',
            background: atRoot ? R.accentSubtle : 'transparent',
          }}
        >
          <Files size={12} color={atRoot ? R.accent : R.textMuted} />
          <span style={{ ...label, color: atRoot ? R.accent : R.textMuted }}>Все документы</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, fontFamily: mono, color: R.textMuted }}>
            {docs.length}
          </span>
        </button>

        {loading && <div style={{ padding: 14, color: R.textMuted, fontSize: 12 }}>Загрузка…</div>}

        {!loading && term && groups.length === 0 && (
          <div style={{ padding: 14, color: R.textMuted, fontSize: 12 }}>Ничего не найдено</div>
        )}

        {groups.map(({ section, docs: list }) => {
          // При активном поиске секции всегда раскрыты.
          const isCollapsed = collapsed.has(section.id) && !term;

          return (
            <div key={section.id}>
              <button
                onClick={() => toggleSection(section.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  width: '100%',
                  padding: '10px 16px 6px',
                  textAlign: 'left',
                }}
              >
                {isCollapsed ? (
                  <ChevronRight size={10} color={R.textMuted} />
                ) : (
                  <ChevronDown size={10} color={R.textMuted} />
                )}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    color: R.textMuted,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {section.name}
                </span>
                <span
                  style={{ marginLeft: 'auto', fontSize: 11, fontFamily: mono, color: R.textMuted }}
                >
                  {list.length}
                </span>
              </button>

              {!isCollapsed &&
                list.map((doc) => {
                  const active =
                    location === `/d/${doc.id}` || location.startsWith(`/d/${doc.id}/`);
                  const Icon = docIcon(doc.icon);

                  return (
                    <button
                      key={doc.id}
                      onClick={() => onNavigate(`/d/${doc.id}`)}
                      onContextMenu={(e) => menu.open(e, docMenu(doc))}
                      title={doc.name}
                      className={`rt-sidebar-doc ${active ? 'rt-sidebar-doc--active' : ''}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        height: 32,
                        padding: '0 16px 0 23px',
                        textAlign: 'left',
                        borderLeft: `3px solid ${active ? R.accent : 'transparent'}`,
                        background: active ? R.accentSubtle : 'transparent',
                        color: active ? R.text : R.textMuted,
                        fontWeight: active ? 600 : 400,
                        fontSize: 13,
                      }}
                    >
                      <Icon size={13} color={active ? R.accent : R.textMuted} />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {doc.name}
                      </span>
                    </button>
                  );
                })}
            </div>
          );
        })}

        <div style={{ height: 8 }} />
      </nav>

      {me && (
        <>
          <div style={{ height: 2, background: R.border }} />

          {me.role === 'admin' && (
            <button
              onClick={() => onNavigate('/portal-admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                height: 38,
                padding: '0 16px',
                width: '100%',
                textAlign: 'left',
                fontSize: 13,
                color: location === '/portal-admin' ? R.accent : R.textSecondary,
                background: location === '/portal-admin' ? R.accentSubtle : 'transparent',
                fontWeight: location === '/portal-admin' ? 700 : 500,
              }}
              onMouseEnter={(e) => {
                if (location !== '/portal-admin') e.currentTarget.style.background = R.bgElevated;
              }}
              onMouseLeave={(e) => {
                if (location !== '/portal-admin') e.currentTarget.style.background = 'transparent';
              }}
            >
              <Globe size={14} color={location === '/portal-admin' ? R.accent : R.textMuted} />
              <span>Гос. реестры (портал)</span>
            </button>
          )}

          <button
            onClick={() => onNavigate('/feedback')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              height: 38,
              padding: '0 16px',
              width: '100%',
              textAlign: 'left',
              fontSize: 13,
              color: location === '/feedback' ? R.accent : R.textMuted,
              background: location === '/feedback' ? R.accentSubtle : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (location !== '/feedback') e.currentTarget.style.background = R.bgElevated;
            }}
            onMouseLeave={(e) => {
              if (location !== '/feedback') e.currentTarget.style.background = 'transparent';
            }}
          >
            <MessageSquare size={14} />
            <span>Предложения и баги</span>
          </button>
        </>
      )}

      {me && (
        <div ref={menuRef} style={{ position: 'relative', borderTop: ft.strong }}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              width: '100%',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: menuOpen ? R.bgElevated : 'transparent',
              color: 'inherit',
              textAlign: 'left',
            }}
          >
            <Avatar name={me.name} src={me.avatarUrl} size={28} color={R.accent} />
            <div style={{ lineHeight: 1.2, overflow: 'hidden', flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: R.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {me.name}
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  color: R.textMuted,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {ROLE_HINT[me.role]}
              </div>
            </div>
            <ChevronDown size={14} color={R.textMuted} />
          </button>

          {menuOpen && (
            <Popover
              style={{
                position: 'absolute',
                bottom: 20,
                left: 'calc(100% - 14px)',
                width: 270,
                boxShadow: shadow.dropdown,
                zIndex: 20,
              }}
            >
              <Label style={{ padding: '8px 12px 6px' }}>{me.name}</Label>

              {me.role === 'admin' && (
                <>
                  <MenuItem
                    icon={Globe}
                    label="Управление реестрами"
                    accent={location === '/portal-admin'}
                    onClick={() => {
                      setMenuOpen(false);
                      onNavigate('/portal-admin');
                    }}
                  />
                  <MenuItem
                    icon={Shield}
                    label="Матрица доступа"
                    accent={location === '/access'}
                    onClick={() => {
                      setMenuOpen(false);
                      onNavigate('/access');
                    }}
                  />
                </>
              )}

              <MenuSeparator />

              <MenuItem
                icon={theme === 'dark' ? Moon : Sun}
                label={`Тема: ${theme === 'dark' ? 'тёмная' : 'светлая'}`}
                hint="Ctrl + Shift + L"
                onClick={() => toggle()}
              />

              <MenuSeparator />

              <MenuItem icon={LogOut} label="Выйти" danger onClick={() => logout.mutate()} />
            </Popover>
          )}
        </div>
      )}
      <menu.Portal />

      {editing && (
        <DocPropertiesModal
          doc={docs.find((d) => d.id === editing)!}
          onClose={() => setEditing(null)}
        />
      )}
    </aside>
  );
}
